<template>
  <v-dialog
    v-model="dialogProxy"
    fullscreen
    scrollable
    persistent
  >
    <v-card class="d-flex flex-column">
      <v-toolbar
        flat
        color="white"
        class="px-5 elevation-1 toolbar toolbar--compact special-rates-toolbar"
        height="48"
      >
        <div class="special-rates-toolbar__info">
          <div class="text-h5 font-weight-bold">
            Tarifas Especiais
          </div>
          <div
            v-if="category && category.description"
            class="subtitle-2 grey--text"
          >
            {{ category.description }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          icon
          class="toolbar-btn"
          @click="handleClose"
        >
          <v-icon color="error">
            {{ icons.mdiClose }}
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 overflow-auto">
        <v-row>
          <v-col
            cols="12"
            md="9"
          >
            <v-alert
              type="info"
              outlined
              dense
              class="mb-6"
            >
              Defina valores diretamente no calendário. Clique em um dia para editar rapidamente ou utilize o formulário lateral para aplicar em períodos maiores.
            </v-alert>

            <v-card
              flat
              class="pa-4 calendar-wrapper"
              outlined
            >
              <div class="calendar-header d-flex justify-space-between align-center mb-4">
                <div>
                  <div class="text-h5 font-weight-bold text-capitalize">
                    {{ calendarTitle }}
                  </div>
                  <div class="text-caption grey--text">
                    Visualize e ajuste as tarifas especiais do período
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    class="toolbar-btn mr-1"
                    @click="calendarPrev"
                  >
                    <v-icon color="primary">
                      {{ icons.mdiChevronLeft }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="toolbar-btn mr-3"
                    @click="calendarNext"
                  >
                    <v-icon color="primary">
                      {{ icons.mdiChevronRight }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    text
                    small
                    color="primary"
                    class="mr-2"
                    @click="calendarToday"
                  >
                    Hoje
                  </v-btn>
                  <v-menu
                    v-model="monthMenu"
                    offset-y
                    :close-on-content-click="false"
                    max-width="320"
                    min-width="320"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        text
                        small
                        class="ml-2"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                      >
                        Escolher mês
                        <v-icon
                          right
                          small
                        >
                          {{ icons.mdiCalendar }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-date-picker
                      v-model="monthPickerValue"
                      type="month"
                      color="primary"
                      @input="selectMonth"
                    ></v-date-picker>
                  </v-menu>
                </div>
              </div>

              <v-calendar
                ref="specialCalendar"
                v-model="calendarFocus"
                type="month"
                color="primary"
                :short-weekdays="true"
                :events="calendarEvents"
                :event-color="getEventColor"
                :event-margin-bottom="8"
                :day-format="dayNumberFormat"
                :day-props="dayProps"
                @click:date="handleDateClick"
                @click:event="handleEventClick"
              >
                <template v-slot:event="{ event }">
                  <div class="tariff-event">
                    <div
                      class="tariff-event__pill"
                      :class="{ 'tariff-event--selected': isDateSelected(event.start) }"
                    >
                      {{ event.name }}
                    </div>
                    <div class="tariff-event__actions">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        :loading="deletingRateId === event.data.id"
                        :disabled="saving"
                        @click.stop="deleteRate(event.data.id)"
                      >
                        <v-icon small>
                          {{ icons.mdiClose }}
                        </v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </v-calendar>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="3"
          >
            <v-card
              outlined
              class="mb-4"
            >
              <v-card-title class="subtitle-1 font-weight-bold">
                Tarifa do dia selecionado
              </v-card-title>
              <v-card-text>
                <div
                  v-if="selectedDate"
                  class="mb-4"
                >
                  <div class="text-caption grey--text text--darken-1">
                    Data selecionada
                  </div>
                  <div class="text-subtitle-1 font-weight-medium text-capitalize">
                    {{ formatDateHuman(selectedDate) }}
                  </div>
                </div>
                <div
                  v-else
                  class="mb-4 grey--text"
                >
                  Clique em uma data do calendário para criar ou editar rapidamente.
                </div>
                <vuetify-money
                  v-model="singleRateForm.price"
                  label="Valor da diária"
                  outlined
                  dense
                  :disabled="!selectedDate || saving"
                />
                <div class="d-flex flex-wrap justify-end gap-2 mt-2">
                  <v-btn
                    text
                    small
                    color="grey"
                    :disabled="!selectedDate"
                    @click="clearSelectedDate"
                  >
                    Limpar
                  </v-btn>
                  <v-btn
                    color="primary"
                    small
                    :disabled="!selectedDate || saving"
                    :loading="saving && singleEditInProgress"
                    @click="saveSingleRate"
                  >
                    Salvar dia
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    outlined
                    :disabled="!selectedDate || !hasSpecialRate(selectedDate)"
                    @click="deleteSpecialRateByDate(selectedDate)"
                  >
                    Remover
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <v-card outlined>
              <v-card-title class="subtitle-1 font-weight-bold">
                Aplicar em período/datas
              </v-card-title>
              <v-card-text>
                <vuetify-money
                  v-model="bulkForm.price"
                  label="Valor da diária"
                  outlined
                  dense
                  :disabled="saving"
                />

                <div class="mb-2 font-weight-medium mt-4">
                  Modo de seleção
                </div>
                <v-btn-toggle
                  v-model="bulkForm.mode"
                  mandatory
                  color="primary"
                  dense
                  rounded
                  class="mb-4"
                >
                  <v-btn value="range">
                    Período contínuo
                  </v-btn>
                  <v-btn value="multiple">
                    Datas específicas
                  </v-btn>
                </v-btn-toggle>

                <v-slide-y-transition>
                  <div v-if="bulkForm.mode === 'range'">
                    <v-menu
                      v-model="rangePickerMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="rangeLabel"
                          label="Período"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="bulkForm.range"
                        range
                        color="primary"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="rangePickerMenu = false"
                        >
                          Concluir
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                  <div v-else>
                    <v-menu
                      v-model="datesPickerMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="320px"
                      min-width="320px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="datesLabel"
                          label="Datas selecionadas"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="bulkForm.dates"
                        multiple
                        color="primary"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="datesPickerMenu = false"
                        >
                          Concluir
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                </v-slide-y-transition>

                <p class="text-caption grey--text mt-4">
                  Use o calendário para visualizar as tarifas já aplicadas e o seletor acima para definir novos períodos ou datas específicas.
                </p>
                <v-btn
                  block
                  color="primary"
                  :loading="saving && !singleEditInProgress"
                  :disabled="saving"
                  @click="submitBulkRate"
                >
                  Aplicar tarifa
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiClose,
} from '@mdi/js'
import { mapActions } from 'vuex'

export default {
  name: 'RatePlanSpecialRatesModal',

  props: {
    value: { type: Boolean, default: false },
    ratePlanId: { type: Number, default: null },
    category: { type: Object, default: () => ({ id: null, description: '' }) },
  },

  data: () => ({
    specialRates: [],
    loading: false,
    saving: false,
    deletingRateId: null,
    singleEditInProgress: false,
    selectedDate: null,
    calendarFocus: new Date().toISOString().substr(0, 10),
    monthMenu: false,
    monthPickerValue: null,
    rangePickerMenu: false,
    datesPickerMenu: false,
    singleRateForm: { price: null },
    bulkForm: {
      mode: 'range',
      price: null,
      range: [],
      dates: [],
    },
    icons: {
      mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiClose,
    },
  }),

  computed: {
    dialogProxy: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },
    specialRateMap() {
      return this.specialRates.reduce((acc, rate) => {
        if (rate && rate.date) acc[rate.date] = rate

        return acc
      }, {})
    },
    calendarEvents() {
      return this.specialRates.map(rate => ({
        name: `R$ ${Number(rate.value).toFixed(2)}`,
        start: rate.date,
        end: rate.date,
        color: 'light-blue lighten-4',
        data: rate,
      }))
    },
    calendarTitle() {
      if (!this.calendarFocus) return ''
      const date = new Date(`${this.calendarFocus}T00:00:00`)

      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    },
    rangeLabel() {
      const range = this.bulkForm.range || []
      const start = Array.isArray(range) ? range[0] : range?.start
      const end = Array.isArray(range) ? range[1] : range?.end
      if (!start || !end) return ''

      return `${new Date(`${start}T00:00:00`).toLocaleDateString('pt-BR')} - ${new Date(`${end}T00:00:00`).toLocaleDateString('pt-BR')}`
    },
    datesLabel() {
      if (!this.bulkForm.dates || !this.bulkForm.dates.length) return ''

      return this.bulkForm.dates.map(d => new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR')).join(', ')
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadRates()
      } else {
        this.resetState()
      }
    },
    monthMenu(value) {
      if (value) {
        this.monthPickerValue = this.calendarFocus ? this.calendarFocus.slice(0, 7) : null
      }
    },
  },

  methods: {
    ...mapActions('rate_plan', {
      listSpecialRates: 'listSpecialRates',
      storeSpecialRates: 'storeSpecialRates',
      removeSpecialRateAction: 'deleteSpecialRate',
    }),

    async loadRates() {
      if (!this.ratePlanId || !this.category?.id) return
      this.loading = true
      try {
        const res = await this.listSpecialRates({ ratePlanId: this.ratePlanId, categoryId: this.category.id })
        this.specialRates = res.data.data.rates || []
        if (this.specialRates.length) {
          this.calendarFocus = this.specialRates[0].date
          this.monthPickerValue = this.calendarFocus.slice(0, 7)
        }
        await this.$nextTick()
        this.selectedDate = null
        this.singleRateForm.price = null
      } catch (e) {
        this.$toast.error('Erro ao carregar tarifas especiais')
      } finally {
        this.loading = false
      }
    },

    resetState() {
      this.specialRates = []
      this.selectedDate = null
      this.singleRateForm.price = null
      this.bulkForm = {
        mode: 'range', price: null, range: [], dates: [],
      }
      this.calendarFocus = new Date().toISOString().substr(0, 10)
      this.deletingRateId = null
      this.saving = false
      this.singleEditInProgress = false
    },

    handleClose() {
      this.$emit('input', false)
      this.$emit('updated')
      this.resetState()
    },

    normalizeDateValue(value) {
      if (!value) return null
      if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10)
      const parsed = new Date(value)

      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
    },

    hasSpecialRate(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(key && this.specialRateMap[key])
    },

    isDateSelected(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(this.selectedDate && key === this.selectedDate)
    },

    handleDateClick(payload) {
      if (!payload) return
      const targetDate = payload.date || payload.day?.date
      if (!targetDate) return
      const normalized = this.normalizeDateValue(targetDate)
      if (!normalized) return
      this.selectedDate = normalized
      const rate = this.specialRateMap[normalized]
      this.singleRateForm.price = rate ? parseFloat(rate.value) : null
    },

    handleEventClick({ event }) {
      if (!event) return
      const normalized = this.normalizeDateValue(event.start)
      if (!normalized) return
      this.selectedDate = normalized
      const rate = this.specialRateMap[normalized]
      this.singleRateForm.price = rate ? parseFloat(rate.value) : null
    },

    clearSelectedDate() {
      this.selectedDate = null
      this.singleRateForm.price = null
    },

    formatDateHuman(date) {
      const normalized = this.normalizeDateValue(date)
      if (!normalized) return ''
      const parsed = new Date(`${normalized}T00:00:00`)
      if (Number.isNaN(parsed.getTime())) return normalized

      return parsed.toLocaleDateString('pt-BR', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      })
    },

    getEventColor() {
      return 'light-blue lighten-4'
    },

    dayNumberFormat({ day }) {
      return day
    },

    dayProps({ date, outside }) {
      return {
        class: [
          outside ? 'outside-day' : '',
          this.isDateSelected(date) ? 'selected-day' : '',
        ].filter(Boolean).join(' '),
      }
    },

    calendarPrev() {
      this.adjustCalendarMonth(-1)
    },

    calendarNext() {
      this.adjustCalendarMonth(1)
    },

    calendarToday() {
      this.calendarFocus = new Date().toISOString().substr(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
    },

    selectMonth(value) {
      const normalized = this.normalizeDateValue(value)
      if (normalized) this.calendarFocus = normalized
      this.monthMenu = false
    },

    adjustCalendarMonth(offset) {
      const base = this.normalizeDateValue(this.calendarFocus) || new Date().toISOString().slice(0, 10)
      const date = new Date(`${base}T00:00:00`)
      date.setUTCDate(1)
      date.setUTCMonth(date.getUTCMonth() + offset)
      this.calendarFocus = date.toISOString().slice(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
    },

    async saveSingleRate() {
      if (!this.selectedDate) {
        this.$toast.error('Selecione uma data no calendário')

        return
      }
      const price = parseFloat(this.singleRateForm.price)
      if (!price || price <= 0) {
        this.$toast.error('Informe o valor da diária')

        return
      }
      this.singleEditInProgress = true
      this.saving = true
      try {
        await this.storeSpecialRates({
          ratePlanId: this.ratePlanId,
          categoryId: this.category.id,
          price,
          dates: [this.selectedDate],
        })
        this.$toast.success('Tarifa diária salva com sucesso')
        await this.loadRates()
      } catch (e) {
        this.$toast.error('Erro ao salvar tarifa do dia')
      } finally {
        this.singleEditInProgress = false
        this.saving = false
      }
    },

    extractBulkDates() {
      if (this.bulkForm.mode === 'multiple') {
        return (this.bulkForm.dates || []).map(d => this.normalizeDateValue(d)).filter(Boolean).sort()
      }
      const range = this.bulkForm.range || []
      const start = Array.isArray(range) ? range[0] : range?.start
      const end = Array.isArray(range) ? range[1] : range?.end
      if (!start || !end) return []

      let startDate = new Date(`${start}T00:00:00Z`)
      let endDate = new Date(`${end}T00:00:00Z`)
      if (startDate > endDate) { const tmp = startDate; startDate = endDate; endDate = tmp }

      const dates = []
      const current = new Date(startDate)
      while (current <= endDate) {
        dates.push(current.toISOString().slice(0, 10))
        current.setUTCDate(current.getUTCDate() + 1)
      }

      return dates
    },

    async submitBulkRate() {
      const price = parseFloat(this.bulkForm.price)
      if (!price || price <= 0) {
        this.$toast.error('Informe o valor da diária')

        return
      }
      const dates = this.extractBulkDates()
      if (!dates.length) {
        this.$toast.error('Selecione ao menos uma data')

        return
      }
      this.singleEditInProgress = false
      this.saving = true
      try {
        await this.storeSpecialRates({
          ratePlanId: this.ratePlanId,
          categoryId: this.category.id,
          price,
          dates,
        })
        this.$toast.success('Tarifa especial aplicada com sucesso')
        await this.loadRates()
        this.bulkForm = {
          mode: this.bulkForm.mode, price: null, range: [], dates: [],
        }
      } catch (e) {
        this.$toast.error('Erro ao salvar tarifa especial')
      } finally {
        this.saving = false
      }
    },

    async deleteRate(rateId) {
      this.deletingRateId = rateId
      try {
        await this.removeSpecialRateAction({
          ratePlanId: this.ratePlanId,
          categoryId: this.category.id,
          rateId,
        })
        this.specialRates = this.specialRates.filter(r => r.id !== rateId)
        const rate = Object.values(this.specialRateMap).find(r => r.id === rateId)
        if (rate && this.selectedDate === rate.date) this.singleRateForm.price = null
        this.$toast.success('Tarifa especial removida')
      } catch (e) {
        this.$toast.error('Erro ao remover tarifa especial')
      } finally {
        this.deletingRateId = null
      }
    },

    deleteSpecialRateByDate(date) {
      const key = this.normalizeDateValue(date)
      const rate = key ? this.specialRateMap[key] : null
      if (!rate) {
        this.$toast.info('Nenhuma tarifa especial cadastrada para esta data')

        return
      }
      this.deleteRate(rate.id)
    },
  },
}
</script>

<style scoped>
.calendar-wrapper {
  background-color: #fff;
  min-height: 540px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tariff-event {
  display: flex;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
}

.tariff-event__pill {
  flex: 1;
  background: linear-gradient(90deg, #e3f2fd, #f1f8ff);
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #0d47a1;
  border: 1px solid rgba(33, 150, 243, 0.2);
  box-shadow: 0 4px 12px rgba(13, 71, 161, 0.08);
}

.tariff-event__pill.tariff-event--selected {
  border-color: rgba(63, 81, 181, 0.7);
  box-shadow: 0 0 0 1px rgba(63, 81, 181, 0.2);
}

.tariff-event__actions {
  display: flex;
  align-items: center;
}

.calendar-wrapper ::v-deep .v-calendar-weekly__day-label,
.calendar-wrapper ::v-deep .v-calendar-weekly__day {
  align-items: flex-start;
}

.calendar-wrapper ::v-deep .outside-day {
  opacity: 0.35;
}

.calendar-wrapper ::v-deep .selected-day .v-calendar-weekly__day-interval {
  border-radius: 12px;
  background: rgba(63, 81, 181, 0.08);
}

.calendar-wrapper ::v-deep .v-calendar-weekly {
  flex: 1;
}

.calendar-wrapper ::v-deep .v-event {
  min-height: 34px;
  width: 100%;
}

.calendar-wrapper ::v-deep .v-event > div {
  height: auto !important;
}

.calendar-header .v-btn {
  text-transform: none;
}

.toolbar .toolbar-btn {
  color: inherit;
}

.toolbar--compact {
  min-height: 48px;
}

.special-rates-toolbar {
  padding-top: 12px;
  padding-bottom: 12px;
}

.special-rates-toolbar__info {
  padding: 6px 0;
}
</style>
